// components/SlotForms/CreateAutomaticSlotForm.jsx
import React, { useEffect, useState } from 'react';
import { Box, Grid, MenuItem, TextField, useTheme, IconButton, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomAutocomplete from 'components/Fileds/CustomAutocomplete';
import CustomTimeTextField from 'components/Fileds/CustomTimeTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomButton from 'components/Fileds/CustomButton';
import { useAddAutomateTimeSlotsMutation } from 'pages/timeSlots/services';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import { isTimeValidForVendor } from 'utils/function';

const INTERVAL_OPTIONS = [30, 45, 60, 75, 90];

const CreateAutomaticSlotForm = ({ categories, onSubmit, handleCloseModal, onAddCategory, initialData, vendorTimings }) => {
    const [slotOptions, setSlotOptions] = useState([]);
    const [autoloading, setAutoLoading] = useState(false);
    const [addAutomateTimeSlotsApi] = useAddAutomateTimeSlotsMutation();
    const theme = useTheme();
    const formik = useFormik({
        initialValues: {
            category: null,
            start_time: null,
            slots_minutes: '',
            slots_count: ''
        },
        validationSchema: Yup.object({
            category: Yup.object().nullable().required('Category is Required'),
            start_time: Yup.date()
                .nullable()
                .required('Start Time is Required')
                .test('is-valid-time', 'Time must be within restaurant operating hours', function (value) {
                    if (!value || !vendorTimings) return true;
                    return isTimeValidForVendor(dayjs(value), vendorTimings, null);
                }),
            slots_minutes: Yup.number().required('Select Minutes'),
            slots_count: Yup.number().when('slots_minutes', {
                is: val => !!val,
                then: schema => schema.required('Slots is Required')
            })
        }),
        // onSubmit: vals => console.log(vals,"values automatic")
        onSubmit: async (vals) => {
            setAutoLoading(true);
            const obj = {
                category_id: vals.category.id,
                start_time: dayjs(vals?.start_time).format('hh:mmA'),
                slots_minutes: vals.slots_minutes,
                slots_count: vals.slots_count,
            }
            try {
                await addAutomateTimeSlotsApi(obj);
                formik.resetForm();
                handleCloseModal();
            } catch (e) {
                console.error(e);
            } finally {
                setAutoLoading(false);
                handleCloseModal()
            }
        },
    });

    useEffect(() => {
        if (initialData) {
            formik.setValues(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        const m = formik.values.slots_minutes;
        const startTime = formik.values.start_time;

        if (m && startTime && vendorTimings) {
            const startHour = startTime.hour();
            const startMinute = startTime.minute();
            const startTotalMinutes = startHour * 60 + startMinute;

            const openDays = vendorTimings.filter(t => t.is_open);

            if (openDays.length > 0) {
                let minPossibleSlots = Math.floor(1440 / m); // Total minutes in a day / interval

                openDays.forEach(day => {
                    let maxSlotsForThisDay = 0;
                    day.slots.forEach(slot => {
                        const [openH, openM] = slot.open_time.split(':').map(Number);
                        const [closeH, closeM] = slot.close_time.split(':').map(Number);
                        const openTotalMinutes = openH * 60 + openM;
                        const closeTotalMinutes = closeH * 60 + closeM;

                        if (startTotalMinutes >= openTotalMinutes && startTotalMinutes < closeTotalMinutes) {
                            const availableMinutes = closeTotalMinutes - startTotalMinutes;
                            const slots = Math.floor(availableMinutes / m);
                            maxSlotsForThisDay = Math.max(maxSlotsForThisDay, slots);
                        }
                    });

                    if (maxSlotsForThisDay < minPossibleSlots) {
                        minPossibleSlots = maxSlotsForThisDay;
                    }
                });

                const max = Math.max(0, minPossibleSlots);
                const opts = Array.from({ length: max }, (_, i) => i + 1);
                setSlotOptions(opts);
                if (formik.values.slots_count > max) {
                    formik.setFieldValue('slots_count', '');
                }
            } else {
                setSlotOptions([]);
                formik.setFieldValue('slots_count', '');
            }
        } else if (m) {
            const max = Math.floor(360 / m);
            const opts = Array.from({ length: max }, (_, i) => i + 1);
            setSlotOptions(opts);
            if (formik.values.slots_count > max) {
                formik.setFieldValue('slots_count', '');
            }
        } else {
            setSlotOptions([]);
            formik.setFieldValue('slots_count', '');
        }
    }, [formik.values.slots_minutes, formik.values.start_time, vendorTimings]);

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ padding: '0px 20px', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CustomTypography variant="subtitle2" label="Select Category"
                        sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0 }}
                    />
                    <Tooltip title="Add Category" arrow placement="top">
                        <IconButton
                            size="small"
                            onClick={() => onAddCategory && onAddCategory(formik.values, 'automatic')}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.common.white,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                                width: 20,
                                height: 20,
                                ml: 1
                            }}
                        >
                            <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <CustomAutocomplete
                    name="category"
                    options={categories}
                    placeholder={'Time Slot Category'}
                    value={formik.values.category}
                    onChange={(_, val) => formik.setFieldValue('category', val)}
                    error={formik.touched.category && formik.errors.category}
                    helperText={formik.touched.category && formik.errors.category}
                    getOptionLabel={opt => opt?.name || ''}
                />
            </Box>
            <Box sx={{ mb: '20px' }}>
                <CustomTypography variant="subtitle2" label="Start time for auto-create slot"
                    sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                />
                <CustomTimeTextField
                    name="start_time"
                    fullWidth
                    value={formik.values.start_time}
                    onChange={val => formik.setFieldValue('start_time', val)}
                    onBlur={formik.handleBlur}
                    error={Boolean(formik.touched.start_time && formik.errors.start_time)}
                    helperText={formik.touched.start_time && formik.errors.start_time}
                    sx={{ height: 45, width: '100%' }}
                    shouldDisableTime={(timeValue) => !isTimeValidForVendor(timeValue, vendorTimings, null)}
                />
            </Box>
            <Box sx={{ mb: '20px' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <CustomTypography variant="subtitle2" label="Duration between slots (minutes)"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                        />
                        <CustomAutocomplete
                            fullWidth
                            name="slots_minutes"
                            placeholder={"Minutes"}
                            options={INTERVAL_OPTIONS}
                            value={formik.values.slots_minutes}
                            onChange={(_, val) => formik.setFieldValue('slots_minutes', val)}
                            error={formik.touched.slots_minutes && formik.errors.slots_minutes}
                            helperText={formik.touched.slots_minutes && formik.errors.slots_minutes}
                            getOptionLabel={opt => opt ? `${opt} minutes` : ''}     // <-- use `opt` directly
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <CustomTypography variant="subtitle2" label="Number of Slots"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                        />
                        <CustomAutocomplete
                            fullWidth
                            name="slots_count"
                            options={slotOptions}
                            placeholder={"Count"}
                            value={formik.values.slots_count}
                            onChange={(_, val) => formik.setFieldValue('slots_count', val)}
                            error={formik.touched.slots_count && formik.errors.slots_count}
                            helperText={formik.touched.slots_count && formik.errors.slots_count}
                            getOptionLabel={opt => `${opt}`}              // <-- again, `opt` is already a number
                            disabled={!formik.values.slots_minutes}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box className='d-flex align-items-center justify-content-center' sx={{ mt: 1 }}>
                <CustomButton
                    type="submit"
                    fullWidth
                    loading={autoloading}
                    disabled={autoloading}
                    sx={{ borderRadius: "10px", width: "180px", height: '45px' }}
                >
                    Create
                </CustomButton>
            </Box>
        </Box>
    );
};

export default CreateAutomaticSlotForm;
